import { GridColDef } from '@mui/x-data-grid';
import BlockIcon from '@mui/icons-material/Block';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Alert,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSetAtom } from 'jotai';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ActionButtons from '../../stateless/Common/ActionButtons';
import { COLORS } from '../../../theme/themeConstants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EditStoreOwnerModal from '../../stateless/EditModel';
import AddMeal from './AddMeal';
import {
  useAllMeals,
  useDeleteMeal,
  usePatchMeal,
} from '../../Query/MealQuery';
import React from 'react';
import { useSnackbar } from '../../stateless/SnackBarProvider';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import { useMealParams } from './useMealParams';
import { useDebounce } from '../../../utils/useDounce';
import { useTranslation } from 'react-i18next';
import { mealAtom, MealDataType } from './types';

interface ManageMealTableProps {
  storeId?: string;
}

const ManageMealTable: React.FC<ManageMealTableProps> = () => {
  const { showMessage } = useSnackbar();
  const { t, i18n } = useTranslation();
  const setMealAtom = useSetAtom(mealAtom);
  const { params, dispatch } = useMealParams();

  const isReady = Boolean(params && Object.keys(params).length > 0);

  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const { data: mealData, isLoading, refetch } = useAllMeals(memoizedParams);
console.log("Meal Data",mealData)
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = '';
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [, forceUpdate] = useState({});

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const { mutate: patchMeal } = usePatchMeal();
  const { mutate: deleteMeal } = useDeleteMeal();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const filteredRows = useMemo(() => {
    const meals = mealData?.data || [];
    const searchLower = params.search?.toLowerCase().trim() || '';
    
    const filtered = meals.filter((row: any) => {
      if (!row) return false;
      const storeName = (row.storeName || '').toLowerCase();
      const storeType = (row.storeType || '').toLowerCase();
      const mealEnglish = (row.mealNameEnglish || '').toLowerCase();
      const mealArabic = (row.mealNameArabic || '').toLowerCase();
      
      return (
        storeName.includes(searchLower) ||
        storeType.includes(searchLower) ||
        mealEnglish.includes(searchLower) ||
        mealArabic.includes(searchLower)
      );
    });

    return filtered.map((row: any, index: any) => {
      if (!row) return { id: `empty-${index}`, sno: index + 1 };
      
      const mealName =
        i18n.language === 'ar'
          ? row.mealNameArabic
          : row.mealNameEnglish;

      // Format choice groups from choiceGroupDetails
      const choiceGroupsText = row.choiceGroupDetails?.map((cg: any) => 
        cg.choiceGroupName || ''
      ).filter(Boolean).join(', ') || '-';

      return {
        ...row,
        id: row.mealId,
        sno: index + 1 + (params.page - 1) * params.limit,
        name: row.storeName || '-',
        storeType: row.storeType,
        mealName: mealName,
        choiceGroupsText: choiceGroupsText,
        choiceGroupsCount: row.choiceGroupDetails?.length || 0,
        status: row.status,
      };
    });
  }, [params.search, mealData, params.page, params.limit, i18n.language]);

  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0)
      return { showBlock: false, showUnblock: false };
    
    const dataList = mealData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.mealId), String(item.status || '')])
    );
    
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedRows, mealData]);

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage(t('meal.messages.selectToBlock'), 'error');
    } else if (window.confirm(t('meal.messages.confirmBlock'))) {
      patchMeal({ id: selectedRows, data: { status: 'blocked' } });
      setSelectedRows([]);
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage(t('meal.messages.selectToUnblock'), 'error');
    } else if (window.confirm(t('meal.messages.confirmUnblock'))) {
      patchMeal({ id: selectedRows, data: { status: 'unblocked' } });
      setSelectedRows([]);
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage(t('meal.messages.selectToDelete'), 'error');
    } else if (window.confirm(t('meal.messages.confirmDelete'))) {
      deleteMeal(selectedRows, {
        onSuccess: () => {
          setSelectedRows([]);
          refetch();
        },
        onError: () => {
          showMessage(t('meal.messages.deleteError'), 'error');
        },
      });
    }
  }, [selectedRows, deleteMeal, showMessage, t]);

  const handleBlockClickButton = async (id: string) => {
    if (window.confirm(t('meal.messages.confirmBlock'))) {
      patchMeal({ id: [id], data: { status: 'blocked' } });
    }
  };

  const handleUnBlockClickButton = async (id: string) => {
    if (window.confirm(t('meal.messages.confirmUnblock'))) {
      patchMeal({ id: [id], data: { status: 'unblocked' } });
    }
  };

  const handleDeleteButton = useCallback(
    (id: string) => {
      if (window.confirm(t('meal.messages.confirmDelete'))) {
        deleteMeal([id]);
      }
    },
    [deleteMeal, t]
  );

  const handleAddMeal = () => setOpenAddDialog(true);
  const handleCloseAddDialog = () => setOpenAddDialog(false);

  const handleEditClick = (row: any) => {
    const mealData: MealDataType = {
      mealId: row.mealId,
      mealNameEnglish: row.mealNameEnglish,
      mealNameArabic: row.mealNameArabic,
      storeId: row.storeId,
      storeName: row.storeName,
      storeType: row.storeType,
      choiceGroupDetails: row.choiceGroupDetails || [],
      price: row.price,
      currency: row.currency,
      status: row.status,
      addedBy: row.addedBy,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
    };
    setMealAtom(mealData);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setMealAtom(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    dispatch({
      type: 'SET_SEARCH',
      payload: debouncedSearch ? debouncedSearch : undefined,
    });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
      setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
      <>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              handleEditClick(row);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('meal.editAction')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              const id = typeof row.id === 'string' ? row.id : '';
              if (id) handleDeleteButton(id);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>
              {t('meal.deleteAction')}
            </ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'sno', headerName: t('meal.columns.sno'), flex: 0.3 },
      { field: 'name', headerName: t('meal.columns.storeName'), flex: 2 },
      { field: 'storeType', headerName: t('meal.columns.storeType'), flex: 1.5 },
      { field: 'mealName', headerName: t('meal.columns.mealName'), flex: 2 },
      {
        field: 'choiceGroupsText',
        headerName: t('meal.columns.choiceGroups'),
        flex: 2,
        renderCell: (params) => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={params.value}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: 'price',
        headerName: t('meal.columns.price'),
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ fontWeight: 'bold' }}>
            {`${params.value || '0'}`}
          </Box>
        ),
      },
      { field: 'addedBy', headerName: t('meal.columns.addedBy'), flex: 1 },
      {
        field: 'status',
        headerName: t('meal.columns.status'),
        flex: 0.5,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const isBlocked = params.value === 'blocked';
          const id = typeof params.row.id === 'string' ? params.row.id : '';
          return (
            <IconButton
              size="small"
              sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
              onClick={() =>
                isBlocked
                  ? handleUnBlockClickButton(id)
                  : handleBlockClickButton(id)
              }
            >
              {isBlocked ? (
                <BlockIcon fontSize="small" />
              ) : (
                <CheckCircleIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
      {
        field: 'actions',
        headerName: t('meal.columns.actions'),
        flex: 0.8,
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => <RowActions row={params.row} />,
      },
    ],
    [t]
  );

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid #e0e0e0',
        marginTop: '20px',
        color: '#666',
      }}
    >
      {snackbarOpen && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
            {snackbarMessage}
          </Alert>
        </Box>
      )}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 600,
            border: '1px solid #ccc',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <TextField
            fullWidth
            placeholder={t('meal.searchPlaceholder')}
            size="medium"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': { height: 40, width: 600, border: 'none' },
              '& fieldset': { border: 'none' },
              '& .MuiInputBase-input': { paddingTop: '10px' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ActionButtons
            onBlock={handleBlockClick}
            onUnblock={handleUnBlockClick}
            onDelete={handleDeleteClick}
            showBlock={showBlock}
            showUnblock={showUnblock}
            showDelete={selectedRows.length > 0}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMeal}
            size="small"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              bgcolor: COLORS.primary.main,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: COLORS.primary.main,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            {t('meal.add')}
          </Button>
        </Box>
      </Box>

      <EditStoreOwnerModal
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        title={t('meal.add')}
      >
        <AddMeal onCancel={handleCloseAddDialog} />
      </EditStoreOwnerModal>

      <EditStoreOwnerModal
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        title={t('meal.edit')}
      >
        <AddMeal onCancel={handleCloseEditDialog} />
      </EditStoreOwnerModal>

      <CommonDataGrid
        key={`datagrid-page-${params.page}-limit-${params.limit}`}
        rows={filteredRows || []}
        columns={columns}
        pageSize={params.limit}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        checkboxSelection
        loading={isLoading}
        page={params.page - 1}
        rowCount={mealData?.pagination?.totalItems || 0}
        components={{
          NoRowsOverlay: () => (
            <ErrorHandler
              type="no-data"
              message={t('meal.noData')}
              onRetry={() => refetch()}
            />
          ),
        }}
        selectionModel={selectedRows}
        onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
      />
    </Box>
  );
};

export default ManageMealTable;
